import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCurrentEmail } from './useCurrentEmail';

export interface AccountPopoverProps {
  pointsBalance: number;
  voucherBalance: number;
  onTopUp: () => void;
  onFeedback: () => void;
  onSignOut: () => void;
}

/**
 * Content for the auth popover anchored to the mobile top-bar auth chip.
 * Composes identity, balances, secondary nav, and sign-out into a single
 * surface so the top bar stays minimal on mobile.
 */
export function AccountPopover(props: AccountPopoverProps): JSX.Element {
  const { pointsBalance, voucherBalance, onTopUp, onFeedback, onSignOut } = props;
  const email = useCurrentEmail(true);

  return (
    <div className="w-64 space-y-3" data-testid="auth-popover">
      <div className="flex items-center gap-3">
        <div
          aria-hidden
          className="flex size-9 items-center justify-center rounded-full bg-accent-primary/15 text-accent-primary text-sm font-semibold"
        >
          {(email[0] ?? '?').toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-small text-text-0 truncate">{email || '(no email)'}</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-small text-text-1">{pointsBalance} pts</span>
        <Button variant="outline" size="sm" onClick={onTopUp}>
          Top up
        </Button>
      </div>

      {voucherBalance > 0 ? (
        <div>
          <Badge variant="outline" data-testid="voucher-balance-popover">
            {voucherBalance} voucher{voucherBalance !== 1 ? 's' : ''}
          </Badge>
        </div>
      ) : null}

      <Separator />

      <nav aria-label="Account menu" className="space-y-2">
        <Link
          to="/account"
          className="block text-small text-text-0 no-underline hover:text-accent-primary py-2"
        >
          Account
        </Link>
        <Link
          to="/about"
          className="block text-small text-text-0 no-underline hover:text-accent-primary py-2"
        >
          About
        </Link>
        <button
          type="button"
          onClick={onFeedback}
          className="block w-full text-left text-small text-text-0 hover:text-accent-primary py-2 px-0 cursor-pointer bg-transparent border-0 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Feedback
        </button>
      </nav>

      <Separator />

      <Button variant="secondary" size="sm" onClick={onSignOut} className="w-full">
        Sign out
      </Button>
    </div>
  );
}
